import { roundQuantity } from '../schedule/unit-of-measure';
import { segmentLength, type Point, type Segment } from './polygon';
import { GEOMETRY_DECIMALS } from './room';
import { isWholeEdge, SPAN_TOLERANCE, type EdgeSpan } from './wall';

/**
 * O trecho do lado que a parede ocupa, e o corte da sobreposição parcial (Fase 24, fatia 8 ·
 * D24 · RN-304 a RN-307 · ADR-157).
 *
 * A parede não guarda geometria (ADR-109): guarda o lado do contorno em que nasceu
 * (RN-243) e, desde esta fatia, que **trecho** dele ocupa. Daqui sai tudo o que precisa do
 * trecho em metros — o pedaço do lado que o desenho pinta, a medida da parede cortada, e o
 * corte em si: onde duas paredes correm juntas e o que sobra de cada uma.
 */

/** Casas do trecho — as mesmas da coluna (`numeric(7, 6)`, na `0047`). */
export const SPAN_DECIMALS = 6;

/**
 * A sobra que não vira parede: **5 cm**.
 *
 * O corte acontece nos cantos do ambiente menor, e o canto vem de um clique. Quem traçou o
 * quarto a dois centímetros do canto da sala não desenhou uma parede de dois centímetros
 * — desenhou a mesma parede, com a mão um pouco fora. A sobra menor que isto fica com o
 * trecho casado. A de verdade mais curta que a obra tem é a ponta da parede que chega em
 * T, com a espessura do bloco (9 a 15 cm), e ela passa.
 */
export const MIN_WALL_PIECE = 0.05;

/** A fração grampeada no lado e arredondada às casas da coluna. */
export function roundSpan(value: number): number {
  return roundQuantity(Math.min(1, Math.max(0, value)), SPAN_DECIMALS);
}

/** O ponto do lado a esta fração do começo dele. */
export function pointAtFraction(segment: Segment, fracao: number): Point {
  return {
    x: segment.from.x + (segment.to.x - segment.from.x) * fracao,
    y: segment.from.y + (segment.to.y - segment.from.y) * fracao,
  };
}

/** O pedaço do lado que o trecho ocupa — o que o desenho pinta como esta parede. */
export function subSegment(segment: Segment, span: EdgeSpan): Segment {
  if (isWholeEdge(span)) return segment;
  return { from: pointAtFraction(segment, span.start), to: pointAtFraction(segment, span.end) };
}

/** Quanto o trecho mede, em metros, arredondado como toda medida do desenho. */
export function spanLength(segment: Segment, span: EdgeSpan): number {
  return roundQuantity(segmentLength(segment) * (span.end - span.start), GEOMETRY_DECIMALS);
}

/** Onde o ponto cai ao longo do lado, em fração — a projeção, sem grampear. */
export function fractionAlong(segment: Segment, point: Point): number {
  const dx = segment.to.x - segment.from.x;
  const dy = segment.to.y - segment.from.y;
  const quadrado = dx * dx + dy * dy;
  if (quadrado === 0) return 0;
  return ((point.x - segment.from.x) * dx + (point.y - segment.from.y) * dy) / quadrado;
}

/**
 * O trecho deste lado que o outro segmento cobre, olhando de frente.
 *
 * É a sombra das duas pontas do outro sobre a reta deste, grampeada ao lado. O sentido não
 * importa — o vizinho traça o contorno dele ao contrário, e a mesma parede chega de cabeça
 * para baixo — e o afastamento também não: quem decide se os dois estão perto o bastante
 * para casar é quem pergunta (o casamento automático tem 30 cm; o feito à mão, mais).
 */
export function projectedSpan(segment: Segment, other: Segment): EdgeSpan | undefined {
  const a = fractionAlong(segment, other.from);
  const b = fractionAlong(segment, other.to);
  const start = roundSpan(Math.min(a, b));
  const end = roundSpan(Math.max(a, b));
  return end - start > SPAN_TOLERANCE ? { start, end } : undefined;
}

/** O pedaço que dois trechos do mesmo lado têm em comum. */
export function intersectSpans(a: EdgeSpan, b: EdgeSpan): EdgeSpan | undefined {
  const start = Math.max(a.start, b.start);
  const end = Math.min(a.end, b.end);
  return end - start > SPAN_TOLERANCE ? { start, end } : undefined;
}

/** Um lado da parede depois do corte: o trecho que casa, e o que sobra antes e depois dele. */
export interface CutSide {
  readonly common: EdgeSpan;
  /** Do começo do trecho da parede até o comum — vira parede livre do mesmo ambiente. */
  readonly before?: EdgeSpan;
  /** Do fim do comum até o fim do trecho da parede — idem. */
  readonly after?: EdgeSpan;
}

/** O corte das duas paredes que correm juntas só num pedaço. */
export interface SharedCut {
  readonly a: CutSide;
  readonly b: CutSide;
  /**
   * O comprimento da parede casada: o trecho comum medido no lado A.
   *
   * Um número só, porque a parede é uma só. Traçados pela face interna, os dois lados
   * medem quase o mesmo; a parede que fica é a do lado A (`MERGE_WALLS` a guarda), e a
   * medida vem de onde ela está.
   */
  readonly length: number;
}

/**
 * O trecho comum dentro do trecho da parede, com o que sobra de cada lado.
 *
 * A sobra mais curta que `minPiece` não vira parede: fica com o comum (ver `MIN_WALL_PIECE`).
 * As fronteiras saem arredondadas uma vez só e repetidas nos dois vizinhos, para o fim de
 * um pedaço ser **exatamente** o começo do outro — é o que permite juntá-los de volta.
 */
function cortarNoLado(edge: Segment, span: EdgeSpan, common: EdgeSpan, minPiece: number): CutSide {
  const medida = segmentLength(edge);
  const antes = (common.start - span.start) * medida;
  const depois = (span.end - common.end) * medida;
  const start = antes < minPiece ? span.start : roundSpan(common.start);
  const end = depois < minPiece ? span.end : roundSpan(common.end);
  return {
    common: { start, end },
    ...(start > span.start ? { before: { start: span.start, end: start } } : {}),
    ...(end < span.end ? { after: { start: end, end: span.end } } : {}),
  };
}

/**
 * Onde duas paredes correm juntas, e o que sobra de cada uma (D24 · RN-305).
 *
 * A parede mais comprida é cortada nos cantos da mais curta: a sala de 6 m com um quarto de
 * 3 m encostado no meio fica com 1,5 m livre, 3 m casada e 1,5 m livre. Quando as duas
 * passam uma da outra — a sala vai de 0 a 6, o quarto de 4 a 8 —, as duas são cortadas.
 *
 * O trecho comum é a sombra de uma sobre a outra, nos dois sentidos: o da sala é onde o
 * quarto cai sobre o lado da sala, e o do quarto é onde a sala cai sobre o lado do quarto.
 * Traçadas pela face interna, a 15 cm uma da outra, as duas sombras são o mesmo trecho
 * visto de cada lado.
 *
 * `undefined` quando não há trecho comum que seja parede: as duas não se cobrem, ou se
 * cobrem menos que `minPiece` — o que é canto, e não divisa.
 */
export function cutShared(
  edgeA: Segment,
  spanA: EdgeSpan,
  edgeB: Segment,
  spanB: EdgeSpan,
  minPiece = MIN_WALL_PIECE,
): SharedCut | undefined {
  const pedacoA = subSegment(edgeA, spanA);
  const pedacoB = subSegment(edgeB, spanB);
  const sombraB = projectedSpan(edgeA, pedacoB);
  const sombraA = projectedSpan(edgeB, pedacoA);
  const naA = sombraB === undefined ? undefined : intersectSpans(spanA, sombraB);
  const naB = sombraA === undefined ? undefined : intersectSpans(spanB, sombraA);
  if (naA === undefined || naB === undefined) return undefined;
  if (spanLength(edgeA, naA) < minPiece || spanLength(edgeB, naB) < minPiece) return undefined;

  const a = cortarNoLado(edgeA, spanA, naA, minPiece);
  const b = cortarNoLado(edgeB, spanB, naB, minPiece);
  return { a, b, length: spanLength(edgeA, a.common) };
}

/** O trecho que cobre os dois — para juntar de volta os pedaços de um lado. */
export function joinSpans(a: EdgeSpan, b: EdgeSpan): EdgeSpan {
  return { start: Math.min(a.start, b.start), end: Math.max(a.end, b.end) };
}

/** Os dois trechos se tocam — o fim de um é o começo do outro? */
export function spansTouch(a: EdgeSpan, b: EdgeSpan): boolean {
  return Math.abs(a.end - b.start) <= SPAN_TOLERANCE || Math.abs(b.end - a.start) <= SPAN_TOLERANCE;
}
